import './styles.css';
import { isProvider, decode, type LocationEntry, type Provider, type Theme } from '@latency-globe/shared';
import { getLocations, getMeta } from './api.ts';
import { Contribute } from './contribute.ts';
import { Colourist, buildLayers, hexToRgb } from './layers.ts';
import { createGlobe, flavorFor, hasWebGL2 } from './map.ts';
import { geolocationAvailable } from './position.ts';
import { forcedBoundaries, reducedMotion, Store, systemTheme, type CellInfo } from './store.ts';
import { Turnstile } from './turnstile.ts';
import { View } from './view.ts';
import { mountBanner } from './ui/banner.ts';
import { mountContribute } from './ui/contribute.ts';
import { mountDetail } from './ui/detail.ts';
import { renderFallback } from './ui/fallback.ts';
import { mountLegend } from './ui/legend.ts';
import { mountTabs } from './ui/tabs.ts';
import { mountTooltip } from './ui/tooltip.ts';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

async function boot(): Promise<void> {
  const hashProvider = location.hash.slice(1);
  const store = new Store({
    provider: isProvider(hashProvider) ? hashProvider : 'cloudflare',
    mode: forcedBoundaries() ? 'boundaries' : 'field',
    theme: systemTheme(),
    build: 0,
    locations: {},
    selected: null,
    hovered: null,
    banner: null,
  });
  document.documentElement.dataset.theme = store.state.theme;

  let meta;
  try { meta = await getMeta(); } catch { meta = null; }
  if (meta) store.set({ build: meta.build });

  if (!hasWebGL2()) {
    let locs: LocationEntry[] | null = null;
    try { locs = meta ? (await getLocations('cloudflare', meta.build)).locations : null; } catch { /* leave null */ }
    renderFallback('This browser has no WebGL2, which the interactive globe needs.', locs);
    return;
  }

  // --- map + layers ----------------------------------------------------------
  let cells: CellInfo[] = [];
  let precision = 2;
  let colourist = new Colourist(new Map(), store.state.theme);
  let mapReady = false;
  const globe = createGlobe($('map'), store.state.theme, () => { mapReady = true; void refresh(); });
  if (import.meta.env.DEV) (window as unknown as { __globe: unknown }).__globe = globe;
  const view = new View(globe.map, (c, p) => { cells = c; precision = p; store.set({ hovered: null }); draw(); }, () => store.set({ banner: 'The map data could not be loaded; showing the basemap only.' }));
  const slow = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (slow && (slow.saveData || slow.effectiveType === '2g' || slow.effectiveType === 'slow-2g')) view.maxPrecision = 3;

  const draw = () => {
    if (!mapReady) return;
    globe.overlay.setProps({
      layers: buildLayers({
        cells,
        colourist,
        mode: store.state.mode,
        theme: store.state.theme,
        surface: hexToRgb(flavorFor(store.state.theme).background),
        beforeId: globe.labelLayerId(),
        version: `${store.state.provider}:${store.state.build}:${precision}:${cells.length}`,
        selected: store.state.selected?.cell ?? null,
        onHover: (info, x, y) => store.set({ hovered: info ? { info, x, y } : null }),
        onClick: (info) => store.set({ selected: info }),
      }),
    });
  };

  const loadLocations = async (provider: Provider) => {
    if (store.state.locations[provider]) return;
    try {
      const res = await getLocations(provider, store.state.build);
      store.set({ locations: { ...store.state.locations, [provider]: new Map(res.locations.map((l) => [l.code, l])) } });
    } catch {
      store.set({ banner: 'Location names could not be loaded; cells will show codes only.' });
    }
  };

  const refresh = async () => {
    await loadLocations(store.state.provider);
    colourist = new Colourist(store.locationsFor(), store.state.theme);
    await view.refresh(store.state.provider, store.state.build);
  };

  globe.map.on('moveend', () => void view.refresh(store.state.provider, store.state.build));

  store.subscribe((s, changed) => {
    if (changed.has('provider')) { cells = []; draw(); void refresh(); }
    if (changed.has('mode') || changed.has('selected')) draw();
    if (changed.has('locations')) { colourist = new Colourist(store.locationsFor(), s.theme); draw(); }
  });

  const setTheme = async (t: Theme) => {
    if (t === store.state.theme) return;
    store.set({ theme: t });
    document.documentElement.dataset.theme = t;
    colourist = new Colourist(store.locationsFor(), t);
    await globe.setTheme(t);
    draw();
  };
  matchMedia('(prefers-color-scheme: light)').addEventListener('change', (e) => void setTheme(e.matches ? 'light' : 'dark'));

  // --- ui ------------------------------------------------------------------
  mountTabs($('tabs'), store);
  mountLegend($('legend'), store, (t) => void setTheme(t));
  mountDetail($('detail'), store, () => colourist);
  mountTooltip($('tooltip'), store, () => colourist);
  mountBanner($('banner'), store);

  // --- contribute ------------------------------------------------------------
  const turnstile = new Turnstile(meta?.turnstile_site_key ?? '', document.body);
  const turnstileOk = meta ? await turnstile.load() : false;
  if (meta && !turnstileOk) store.set({ banner: 'Human verification could not load, so contributing is unavailable. The map still works.' });
  const flow = new Contribute(turnstile);
  flow.subscribe((s) => { if (s.kind !== 'idle') globe.stopIdleSpin(); });
  // A new provider is a new offer: drop any run or outcome and show the button again.
  store.subscribe((_s, changed) => { if (changed.has('provider')) flow.reset(); });
  mountContribute($('contribute'), store, flow, {
    available: turnstileOk && geolocationAvailable(),
    onSubmitted: (s) => {
      const c = decode(s.cell);
      const opts = { center: [c.lon, c.lat] as [number, number], zoom: 9.5 };
      if (reducedMotion()) globe.map.jumpTo(opts); else globe.map.flyTo({ ...opts, duration: 2200, essential: true });
    },
  });

  if (!meta) store.set({ banner: 'The API is unreachable; showing the basemap only.' });
}

void boot();
